import { addDaysIso, isIsoDate, maxIso, todayIso } from './iso-date';

/**
 * Resultado discriminado de validar un rango `[inicio, fin]` contra
 * cotas opcionales. El orden de chequeo (y por tanto el motivo de
 * fallo que se reporta) es:
 *   1. `incompleto`        — falta alguna fecha o no es ISO.
 *   2. `anterior-a-hoy`    — pediste `noAnteriorAHoy` e `inicio < hoy`.
 *   3. `invertido`         — `fin <= inicio` cuando se exige estricto;
 *                            `fin < inicio` en modo no estricto.
 *   4. `fuera-de-rango`    — cae fuera de `[min, max]`.
 *   5. `ok`                — pasa todas las reglas.
 */
export type ValidacionRango =
  | 'incompleto'
  | 'anterior-a-hoy'
  | 'invertido'
  | 'fuera-de-rango'
  | 'ok';

export interface ValidarRangoOptions {
  /** Cota inferior para `inicio`. */
  min?: string | undefined;
  /** Cota superior para `fin`. */
  max?: string | undefined;
  /** Si `true`, exige `inicio >= hoy`. Default `false`. */
  noAnteriorAHoy?: boolean;
  /**
   * Si `true`, exige `fin > inicio` (estricto). Default `false`
   * (`fin >= inicio`). Útil para subfases donde no se permite que
   * inicio y fin caigan el mismo día.
   */
  finEstrictamenteMayor?: boolean;
}

/**
 * Validador único de rango de fechas. Usado por SubfaseCard,
 * AgregarSubfaseDialog y validaciones del wizard. Devuelve un
 * discriminador que la UI traduce a mensajes específicos.
 */
export function validarRango(
  inicio: string | undefined | null,
  fin: string | undefined | null,
  options: ValidarRangoOptions = {},
): ValidacionRango {
  if (!isIsoDate(inicio) || !isIsoDate(fin)) return 'incompleto';
  if (options.noAnteriorAHoy && inicio < todayIso()) return 'anterior-a-hoy';
  if (options.finEstrictamenteMayor ? fin <= inicio : fin < inicio) return 'invertido';
  if (isIsoDate(options.min) && inicio < options.min) return 'fuera-de-rango';
  if (isIsoDate(options.max) && fin > options.max) return 'fuera-de-rango';
  return 'ok';
}

/** Atajo booleano de `validarRango(...) === 'ok'`. */
export function rangoOk(
  inicio: string | undefined | null,
  fin: string | undefined | null,
  options: ValidarRangoOptions = {},
): boolean {
  return validarRango(inicio, fin, options) === 'ok';
}

/**
 * Cota `notBefore` para el `DatePicker` de **inicio**: el máximo
 * entre hoy y la cota dura `min` que provea el caller (fecha del
 * proyecto, fin máximo de la fase anterior, etc.). Nunca deja
 * seleccionar fechas pasadas.
 */
export function notBeforeInicio(min?: string): string {
  return maxIso(todayIso(), min) ?? todayIso();
}

/**
 * Cota `notBefore` para el `DatePicker` de **fin**:
 * - Si el inicio ya está seteado y se exige `fin > inicio` (estricto),
 *   se retorna `inicio + 1 día` para que el calendario no permita
 *   seleccionar el mismo día del inicio.
 * - Si el inicio ya está seteado y se permite `fin >= inicio`
 *   (default), se retorna `inicio` tal cual.
 * - Si el inicio aún no es ISO, se cae al `fallback` (que puede ser
 *   `undefined` cuando no hay cota inferior dura — p. ej. subfases de
 *   proyectos históricos cuyas fechas pueden ser pasadas).
 */
export function notBeforeFin(
  inicio: string | undefined | null,
  fallback: string | undefined,
  options: { estricto?: boolean } = {},
): string | undefined {
  if (!isIsoDate(inicio)) return fallback;
  return options.estricto ? addDaysIso(inicio, 1) : inicio;
}
